/**
 * API service using axios
 */
import axios from 'axios';

// Force HTTP in development (prevent HSTS issues)
const getBaseURL = () => {
  const envURL = process.env.REACT_APP_API_BASE_URL;
  const defaultURL = 'http://localhost:8000/api/v1';

  // Force HTTP in development (no HTTPS on localhost)
  const baseURL = envURL || defaultURL;

  // Ensure we're using HTTP not HTTPS for localhost
  if (baseURL.includes('localhost') && baseURL.startsWith('https://')) {
    console.warn('WARNING: Forcing HTTP instead of HTTPS for localhost');
    return baseURL.replace('https://', 'http://');
  }

  return baseURL;
};

// Create axios instance
const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
  // SECURITY: Enable credentials to send httpOnly cookies (XSS protection)
  withCredentials: true,
});

// SECURITY: Hybrid approach - send BOTH cookies AND Authorization header
// - Cookies work for same-domain deployments (more secure)
// - Authorization header works for cross-domain deployments (compatibility)
// - Backend accepts either method
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const baseURL = getBaseURL().replace('/api/v1', '');
          const response = await axios.post(
            `${baseURL}/api/v1/auth/refresh`,
            { refresh_token: refreshToken }
          );

          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('lastActivity', Date.now().toString());

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('lastActivity');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
